import React, { useEffect } from "react";
import { Palmtree, Briefcase, Clock, ChevronRight, Loader2 } from "lucide-react";
import { DateTimeUtil } from "../../../utils";
import { useNavigate } from "react-router";
import { startOfWeek, endOfWeek, eachDayOfInterval, format, subDays, addDays } from "date-fns";
import { getTimeOffRequestList } from "../../../redux/features/timeoff";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { Timeoff } from "../../../types";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout/slice";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { getCachedData, setCachedData } from "../../../utils/storageCache";

// Cache keys
const TIMEOFF_CACHE_KEY = 'timeoff_tile_data';
const TIMEOFF_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const TimeOffTile: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { setSourcePath } = useNavigationHistory();
    const user = useSelector((state: RootState) => state.user.user);
    const { isLoadingTimeOffRequests, fetchedTimeoffRequests } = useSelector((state: RootState) => state.timeoff);

    const getIcon = (type: string) => {
        switch (type) {
            case "Vacation":
                return <Palmtree className="w-6 h-6 text-green-500 mr-3" />;
            case "Sick":
                return <Briefcase className="w-6 h-6 text-red-500 mr-3" />;
            case "Comp/In Lieu":
                return <Clock className="w-6 h-6 text-blue-500 mr-3" />;
            default:
                return <Clock className="w-6 h-6 text-gray-500 mr-3" />;
        }
    };

    const navToTimeoffScreen = () => {
        dispatch(setTopNavNameTitle("Time Off & Balances"));
        // Set the source path to mobile-dashboard before navigating
        setSourcePath("/mobile-dashboard");
        navigate("/m-timeoff", { state: { from: "/mobile-dashboard" } });
    };

    useEffect(() => {
        if (user?.id === -1) return;

        // Try to get cached data first
        const cachedTimeoff = getCachedData<{requests: Timeoff[]}>('TIMEOFF_CACHE_KEY', TIMEOFF_CACHE_DURATION);
        if (cachedTimeoff) {
            // Use cached data
            return;
        }

        const dateRange = getDateRange();
        const getTimeOffRequestsBody = {
            start_date: dateRange[0],
            end_date: dateRange[dateRange.length - 1],
            employee_id: user.id!.toString(),
            category_code: "",
        };

        dispatch(getTimeOffRequestList(getTimeOffRequestsBody));
    }, [dispatch, user]);

    // Cache the fetched data when it arrives
    useEffect(() => {
        if (fetchedTimeoffRequests) {
            setCachedData(TIMEOFF_CACHE_KEY, { requests: fetchedTimeoffRequests });
        }
    }, [fetchedTimeoffRequests]);

    const getDateRange = (): string[] => {
        const today = new Date();
        const start = subDays(today, 30); // 30 days before today
        const end = addDays(today, 30); // 30 days after today

        return eachDayOfInterval({ start, end }).map((date) => format(date, "yyyy-MM-dd"));
    };

    const today = DateTimeUtil.formatDate(new Date(), "yyyy-MM-dd");
    const tomorrow = DateTimeUtil.formatDate(addDays(new Date(), 1), "yyyy-MM-dd");

    // Get data either from cache or from redux state
    const getTimeOffData = () => {
        const cachedData = getCachedData<{requests: Timeoff[]}>('TIMEOFF_CACHE_KEY', TIMEOFF_CACHE_DURATION);
        return cachedData?.data?.requests || fetchedTimeoffRequests;
    };

    const upcomingTimeOffs = getTimeOffData()?.filter((request: Timeoff) => {
        const startDate = new Date(request.start_date!);
        const endDate = new Date(request.end_date!);

        return (
            DateTimeUtil.isSameDayWithDateFns(startDate, new Date(today)) ||
            DateTimeUtil.isSameDayWithDateFns(endDate, new Date(tomorrow)) ||
            (startDate <= new Date(today) && endDate >= new Date(today)) || // Covers today being within the range
            (startDate <= new Date(tomorrow) && endDate >= new Date(tomorrow)) // Covers tomorrow being within the range
        );
    });

    const formatDateRange = (start: string, end: string): string => {
        const startDate = new Date(start);
        const endDate = new Date(end);

        const startMonth = format(startDate, "MMM");
        const endMonth = format(endDate, "MMM");

        const startDay = format(startDate, "d");
        const endDay = format(endDate, "d");

        if (startDay === endDay) {
            return `${startMonth} ${startDay}`;
        }

        return startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    };
    return (
        <div className="mt-4 max-w-md mx-auto border border-gray-300 bg-white rounded-lg p-6">
            <div className="flex items-center justify-between">
                <h2
                    className="text-xl font-semibold text-blue-500 leading-none flex items-center"
                    onClick={navToTimeoffScreen}
                >
                    {/* Upcoming Time Off */}
                    Planned Time Off
                </h2>
                <ChevronRight className="w-7 h-7 flex-shrink-0 text-blue-500" onClick={navToTimeoffScreen} />
            </div>

            <div className="mt-4">
                {isLoadingTimeOffRequests ? (
                    <div className="flex justify-center py-4">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                ) : upcomingTimeOffs?.length > 0 ? (
                    <div className="space-y-4">
                        {upcomingTimeOffs.map((timeOff, index) => {
                            const isToday = DateTimeUtil.isSameDayWithDateFns(
                                new Date(timeOff.start_date!),
                                new Date()
                            );

                            return (
                                <div
                                    key={index}
                                    className={`flex items-center justify-between ${
                                        isToday ? "bg-blue-50 rounded-lg border border-blue-200 p-2" : ""
                                    }`}
                                >
                                    <div className="flex flex-col items-start">
                                        <span
                                            className={`text-gray-700 font-medium ${
                                                isToday ? "text-blue-600 font-bold" : ""
                                            }`}
                                        >
                                            {formatDateRange(timeOff.start_date!, timeOff.end_date!)}
                                        </span>
                                        <span className="text-gray-500 text-sm">
                                            {DateTimeUtil.getDayName(new Date(timeOff.start_date!))}
                                        </span>
                                    </div>
                                    <div className="flex items-center">
                                        {getIcon(timeOff.category_code!)}
                                        <span className="text-gray-700 font-medium">
                                            {timeOff.hours_requested} {timeOff.track_time_in == "Days" ? "Days" : "h"}
                                        </span>
                                    </div>
                                    <div className="flex items-center">
                                        <span className="text-gray-700 font-medium">{timeOff.category_name}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-gray-500 font-medium">No upcoming time-offs</div>
                )}
            </div>
        </div>
    );
};

export default TimeOffTile;
